import React from 'react';
import { useMapCanvas } from '../map/contexts/MapCanvasContext';
import {
  LayerType,
  Shape,
  RectangleShape,
  CircleShape,
  TextShape,
  SVGShape,
  IconShape,
  FurnitureShape,
  ImageShape,
  SpotShape
} from '../../types';
import { SpotShapeComponent } from '../map/shapes/SpotShape/SpotShape';
import { IconShape as IconShapeComponent } from '../map/shapes/IconShape/IconShape';
import { FurnitureShape as FurnitureShapeComponent } from '../map/shapes/FurnitureShape/FurnitureShape';
import { ShapeProvider } from '../map/shapes/ShapeContext/ShapeProvider';
import { SVGShapeRenderer } from './SVGShapeRenderer';
import { MultilineSvgText } from './MultilineSvgText';

export interface ViewSVGProps {
  showOutside?: boolean;
  style?: React.SVGProps<SVGSVGElement>['style'];
}

const renderShapeToSVG = (shape: Shape): React.ReactElement | null => {
  const baseProps = { x: shape.x, y: shape.y };
  const opacity = 1;
  const opacityAttr = opacity !== 1 ? { opacity } : {};

  // Common ShapeProvider props for all shapes that need it
  const shapeProviderProps = {
    key: shape.id,
    shape,
    isSelected: false,
    isInSelection: false,
    onSelect: () => {},
    onUpdate: () => {},
    snapToGrid: false,
    gridSize: 20,
    otherShapes: [],
    showSnapLines: false,
    disabled: false,
    isMultiSelected: false,
    onDelete: () => {},
    onLocate: () => {},
    onMoveUp: () => {},
    onMoveDown: () => {}
  };

  // Common component props for shapes that need them
  const componentProps = {
    id: shape.id,
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation || 0,
    draggable: false,
    onClick: () => {},
    onTap: () => {},
    onDragStart: () => {},
    onDragMove: () => {},
    onDragEnd: () => {},
    onTransformEnd: () => {},
    onMouseEnter: () => {},
    onMouseLeave: () => {},
    onContextMenu: () => {},
    ref: null
  };

  const renderShapeContent = () => {
    switch (shape.type) {
      case 'rectangle': {
        const rectShape = shape as RectangleShape;
        return (
          <rect
            {...baseProps}
            width={rectShape.width}
            height={rectShape.height}
            fill={rectShape.color || '#000000'}
            {...opacityAttr}
            rx={rectShape.cornerRadius || 0}
          />
        );
      }

      case 'circle': {
        const circleShape = shape as CircleShape;
        const cx = circleShape.x + circleShape.width;
        const cy = circleShape.y + circleShape.height;
        return (
          <ellipse
            cx={cx}
            cy={cy}
            rx={circleShape.width}
            ry={circleShape.height}
            fill={circleShape.color || '#000000'}
            {...opacityAttr}
          />
        );
      }

      case 'text': {
        const textShape = shape as TextShape;
        const fontSize = textShape.fontSize || 16;
        const fontFamily = textShape.fontFamily || 'Arial';
        const fontWeight = textShape.isBold ? 'bold' : 'normal';
        const fontStyle = textShape.isItalic ? 'italic' : 'normal';
        const textDecoration = textShape.isUnderline ? 'underline' : 'none';
        const textColor = textShape.textColor || '#000000';
        const textAlign = textShape.textAlign || 'left';

        // Calculate text anchor based on alignment
        let textAnchor = 'start';
        if (textAlign === 'center') textAnchor = 'middle';
        else if (textAlign === 'right') textAnchor = 'end';

        return (
          <MultilineSvgText
            width={textShape.width}
            style={{
              fontSize: `${fontSize}px`,
              fontFamily: fontFamily,
              fontWeight: fontWeight,
              fontStyle,
              textDecoration,
              fill: textColor
            }}
            textAnchor={textAnchor as never}
            height={textShape.height}
            fontFamily={fontFamily}
            {...baseProps}
          >
            {textShape.text}
          </MultilineSvgText>
        );
      }

      case 'svg': {
        const svgShape = shape as SVGShape;
        return <SVGShapeRenderer shape={svgShape} />;
      }

      case 'icon': {
        const iconShape = shape as IconShape;
        return <IconShapeComponent {...componentProps} renderMode="svg" />;
      }

      case 'furniture': {
        const furnitureShape = shape as FurnitureShape;
        return <FurnitureShapeComponent {...componentProps} renderMode="svg" />;
      }

      case 'image': {
        const imageShape = shape as ImageShape;
        return (
          <image
            {...baseProps}
            width={imageShape.width}
            height={imageShape.height}
            href={imageShape.path}
            {...opacityAttr}
          />
        );
      }

      case 'spot': {
        const spotShape = shape as SpotShape;
        return <SpotShapeComponent {...componentProps} renderMode="svg" />;
      }

      default:
        return null;
    }
  };

  // Wrap shapes that need ShapeProvider in it, others return directly
  if (
    shape.type === 'icon' ||
    shape.type === 'furniture' ||
    shape.type === 'spot'
  ) {
    return (
      <ShapeProvider {...shapeProviderProps}>
        {renderShapeContent()}
      </ShapeProvider>
    );
  }

  return renderShapeContent();
};

const getShapesByLayer = (shapes: Shape[], layerType: LayerType): Shape[] => {
  return shapes.filter((shape) => shape.layerType === layerType);
};

export const ViewSVG = React.forwardRef<SVGSVGElement, ViewSVGProps>(
  ({ showOutside = true, style }, ref) => {
    const { shapes, editingZoneWidth, editingZoneHeight, stagePosition } =
      useMapCanvas();
    const width = editingZoneWidth;
    const height = editingZoneHeight;

    // Apply stage transformations
    const transform = `translate(${stagePosition.x}, ${stagePosition.y}) scale(${stagePosition.scale})`;

    // Memoize shape processing since shapes won't change in view mode
    const processedShapes = React.useMemo(() => {
      // Get shapes by layer
      const floorShapes = getShapesByLayer(shapes, 'floor');
      const backgroundShapes = getShapesByLayer(shapes, 'background');
      const shapeShapes = getShapesByLayer(shapes, 'shapes');
      const spotShapes = getShapesByLayer(shapes, 'spots');

      return {
        floorShapes,
        backgroundShapes,
        shapeShapes,
        spotShapes
      };
    }, [shapes, stagePosition, editingZoneWidth, editingZoneHeight]);

    // Memoize bottom layer rendering
    const bottomLayerSVG = React.useMemo(() => {
      const bottomLayerShapes = [...processedShapes.floorShapes];
      return bottomLayerShapes
        .map((shape) => {
          return renderShapeToSVG(shape);
        })
        .filter(Boolean);
    }, [processedShapes.floorShapes, processedShapes.backgroundShapes]);

    // Memoize top layer rendering
    const topLayerSVG = React.useMemo(() => {
      const topLayerShapes = [...processedShapes.shapeShapes];
      return topLayerShapes
        .map((shape) => renderShapeToSVG(shape))
        .filter(Boolean);
    }, [processedShapes.shapeShapes]);

    const stopLayerSVG = React.useMemo(() => {
      const topLayerShapes = [...processedShapes.spotShapes];
      return topLayerShapes
        .map((shape) => renderShapeToSVG(shape))
        .filter(Boolean);
    }, [processedShapes.spotShapes]);

    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        className={'injected-svg'}
        ref={ref}
        style={style}
      >
        {/* Bottom layer: Floor and Background shapes */}
        <g transform={transform}>{bottomLayerSVG}</g>

        <g transform={transform}>{topLayerSVG}</g>
        <g id={'resources'} transform={transform}>
          {stopLayerSVG}
        </g>
      </svg>
    );
  }
);
